import React, { useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowRight, ShoppingBag } from 'lucide-react';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { useAuth } from '../../../contexts/AuthContext';
import { useCart } from '../../../contexts/CartContext';
import { shopData } from './shopData';
import { createHostedCheckout, updateHostedCheckoutShipping } from '../../../utils/firebase';
import { STRIPE_PUBLISHABLE_KEY } from '../../../utils/stripe';
import type { CartItem } from '../../../contexts/CartContext';
import type { CheckoutLineItemInput } from '../../../utils/stripe';
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY);
function getCheckoutRefFor(item: CartItem): Pick<CheckoutLineItemInput, 'priceId' | 'lookupKey'> | null {
  if (item.lookupKey) {
    return {
      lookupKey: item.lookupKey
    };
  }
  if (item.stripePriceId) {
    return {
      priceId: item.stripePriceId
    };
  }
  const supplement = shopData.find(product => product.id === item.productId);
  if (supplement?.lookupKey) {
    return {
      lookupKey: supplement.lookupKey
    };
  }
  return null;
}
export function CheckoutV2() {
  const {
    user,
    loading: authLoading
  } = useAuth();
  const {
    items,
    clearCart
  } = useCart();
  const navigate = useNavigate();
  const sessionIdRef = useRef<string | null>(null);
  const errorRef = useRef<string | null>(null);
  const lineItems = useMemo(() => {
    return items.map(item => {
      const ref = getCheckoutRefFor(item);
      if (!ref) return null;
      return {
        ...ref,
        quantity: item.quantity
      };
    }).filter((item): item is NonNullable<typeof item> => item !== null);
  }, [items]);
  const hasPhysicalItems = useMemo(() => items.some(item => item.type === 'supplement'), [items]);
  const fetchClientSecret = useCallback(async () => {
    window.sessionStorage.removeItem('sn_checkout_clear_pending');
    window.sessionStorage.removeItem('sn_inventory_checkout_session');
    window.sessionStorage.removeItem('sn_checkout_shipping_address');
    if (lineItems.length !== items.length) {
      throw new Error('One or more items in your cart are not available for checkout yet.');
    }
    const result = await createHostedCheckout({
      lineItems,
      hasPhysicalItems
    });
    sessionIdRef.current = result.sessionId;
    window.sessionStorage.setItem('sn_inventory_checkout_session', result.sessionId);
    return result.clientSecret;
  }, [hasPhysicalItems, items.length, lineItems]);
  const onShippingDetailsChange = useCallback(async (event: any) => {
    try {
      const checkoutSessionId = event.checkoutSessionId ?? sessionIdRef.current;
      if (!checkoutSessionId) {
        return {
          type: 'reject' as const,
          errorMessage: 'Checkout session is not ready yet.'
        };
      }
      await updateHostedCheckoutShipping({
        checkoutSessionId,
        shippingDetails: event.shippingDetails
      });
      return {
        type: 'accept' as const
      };
    } catch (err: any) {
      console.error('[CheckoutV2] Could not update shipping:', err);
      return {
        type: 'reject' as const,
        errorMessage: err?.message ?? 'We could not calculate shipping for that address.'
      };
    }
  }, []);
  const onComplete = useCallback(() => {
    const sessionId = sessionIdRef.current;

    // Payment succeeded, so clear the cart now.
    clearCart();
    window.sessionStorage.setItem('sn_checkout_clear_pending', 'true');
    const query = new URLSearchParams({
      order: 'success'
    });
    if (sessionId) {
      query.set('session_id', sessionId);
    }
    if (user) {
      navigate(`/account?${query.toString()}`, {
        replace: true
      });
    } else {
      navigate(`/order-confirmation?${query.toString()}`, {
        replace: true
      });
    }
  }, [clearCart, navigate, user]);
  const options = useMemo(() => ({
    fetchClientSecret,
    onShippingDetailsChange,
    onComplete
  }), [fetchClientSecret, onShippingDetailsChange, onComplete]);
  if (!authLoading && items.length === 0) {
    return <div className="w-full min-h-screen flex flex-col items-center justify-center pt-32 pb-20 px-6 bg-white" data-id="element-2357" data-mp="2i9gv2d5kad26">
        <div className="max-w-2xl mx-auto text-center" data-id="element-2358" data-mp="2n78kn0iqhn37">
          <div className="w-20 h-20 bg-white border border-[#E5E5E5] shadow-sm rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-2359" data-mp="3aye5ju3j75kd">
            <ShoppingBag className="w-8 h-8 text-[#9A9A90]" data-id="element-2360" data-mp="3frgyc00hiazi" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#111111] tracking-tight mb-4" data-id="element-2361" data-mp="3p2msswuo4mry">
            Your cart is empty.
          </h1>
          <p className="text-sm font-medium text-[#5A5A5A] mb-10" data-id="element-2362" data-mp="2oligiylkq3y">
            Add a supplement to begin checkout.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-2363" data-mp="3tumqein9n3ld">
            <Link to="/shop" className="px-8 py-4 bg-tealslate text-white text-sm font-bold hover:bg-navyslate transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-2364" data-mp="36520tgwlx834">
              Shop Supplements
              <ArrowRight className="w-4 h-4" data-id="element-2365" data-mp="3g154wsb0rgsp" />
            </Link>
            <Link to="/shop" className="px-8 py-4 bg-white border border-[#E5E5E5] text-[#111111] hover:border-tealslate hover:text-tealslate text-sm font-bold transition-colors inline-flex items-center justify-center gap-2 rounded-md shadow-sm" data-id="element-2366" data-mp="3b2tuoggtb49y">
              Shop Supplements
              <ArrowRight className="w-4 h-4" data-id="element-2367" data-mp="ygydy23p2kh0" />
            </Link>
          </div>
        </div>
      </div>;
  }
  if (authLoading) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex items-center justify-center px-6" data-id="element-2368" data-mp="17gv5cbhgxxfr">
        <div className="max-w-md w-full text-center" data-id="element-2369" data-mp="1cdlh4mm8q6vd">
          <span className="w-8 h-8 border-2 border-tealslate/30 border-t-tealslate rounded-full animate-spin inline-block mb-4" data-id="element-2370" data-mp="eqwd677z9k6v" />
          <h1 className="text-xl font-bold text-[#111111] mb-2" data-id="element-2371" data-mp="jp7nsk63jyq5">
            Preparing secure checkout…
          </h1>
        </div>
      </div>;
  }
  if (errorRef.current) {
    return <div className="w-full min-h-screen bg-[#F9F9F9] flex items-center justify-center px-6" data-id="element-2372" data-mp="tgu42uxaknzr">
        <div className="max-w-md w-full text-center" data-id="element-2373" data-mp="3s4raf0c9iiw8">
          <AlertCircle className="w-10 h-10 mx-auto mb-4 text-red-600" data-id="element-2374" data-mp="3s3y9ifzzinm0" />
          <h1 className="text-2xl font-bold text-[#111111] mb-3" data-id="element-2375" data-mp="xn64d7i5ari">
            Checkout could not start.
          </h1>
          <p className="text-sm text-[#5A5A5A] mb-6" data-id="element-2376" data-mp="3pj0b50tif5gr">
            {errorRef.current}
          </p>
          <button type="button" onClick={() => window.location.reload()} className="px-6 py-3 bg-tealslate text-white text-sm font-bold rounded-md" data-id="element-2377" data-mp="3kkf7ssiyo5o3">
            Try Again
          </button>
        </div>
      </div>;
  }
  return <div className="w-full min-h-screen bg-white pt-24 pb-24" data-id="element-2378" data-mp="36obklj6i8xl4">
      <EmbeddedCheckoutProvider stripe={stripePromise} options={options} data-id="element-2379" data-mp="31v0czftvccs5">
        <EmbeddedCheckout data-id="element-2380" data-mp="2wxguu4u1vfux" />
      </EmbeddedCheckoutProvider>
    </div>;
}